import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { useTranslation, useChannels, useInboxTeams } from '@hooks';
import { AssignedStatus } from '@constants/conversations';
import { CHANNEL } from '@constants/channels';
import { getChannelIcon } from '@helpers/channels';
import {
    RailContainer,
    RailHead,
    SectionTitle,
    SectionDivider,
    RailItemButton,
    RailItemLabel,
    RailItemCount,
    TagDotSlot,
} from './styles';

const RAIL_COLLAPSED_KEY = 'inbox-rail-collapsed';

const getInitialCollapsed = () => {
    try {
        return localStorage.getItem(RAIL_COLLAPSED_KEY) === 'true';
    } catch {
        return false;
    }
};

const RailItem = ({ icon, label, count, active, collapsed, onClick }) => {
    const item = (
        <RailItemButton active={active} collapsed={collapsed} onClick={onClick}>
            {icon}
            {!collapsed && <RailItemLabel active={active}>{label}</RailItemLabel>}
            {!collapsed && (count ?? null) !== null && <RailItemCount active={active}>{count}</RailItemCount>}
        </RailItemButton>
    );
    return collapsed ? (
        <Tooltip disableInteractive title={label} placement="right">
            {item}
        </Tooltip>
    ) : (
        item
    );
};

const NavigationRail = ({ filter, scopeHandlers, variant = 'rail', onItemSelected }) => {
    const [collapsed, setCollapsed] = useState(variant === 'rail' ? getInitialCollapsed : false);

    const { counters, archived } = useSelector(state => state.conversations);
    const { data: conversationTags } = useSelector(state => state.conversationTags);

    const { t } = useTranslation();
    const { channels, getAccessibleChannelIds } = useChannels();
    const { teams } = useInboxTeams();
    // The grid columns already narrow at this breakpoint; the rail collapses with
    // them instead of squeezing the thread
    const isNarrow = useMediaQuery('@media (max-width: 1480px)');

    const isDrawer = variant === 'drawer';
    const isCollapsed = !isDrawer && (collapsed || isNarrow);

    const toggleCollapsed = () => {
        setCollapsed(prev => {
            const next = !prev;
            try {
                localStorage.setItem(RAIL_COLLAPSED_KEY, String(next));
            } catch {
                // ignore
            }
            return next;
        });
    };

    const incomingChannels = useMemo(() => {
        const accessibleIds = new Set(getAccessibleChannelIds(c => c?.incoming));
        return channels.filter(channel => accessibleIds.has(channel.id));
    }, [channels, getAccessibleChannelIds]);

    const teamCounts = useMemo(() => {
        const map = new Map();
        (counters?.teams || []).forEach(item => map.set(item.teamId, item.open));
        return map;
    }, [counters?.teams]);

    const tagCounts = useMemo(() => {
        const map = new Map();
        (counters?.tags || []).forEach(item => map.set(item.tagId, item.open));
        return map;
    }, [counters?.tags]);

    const activeTeamId = filter.teams?.length === 1 ? filter.teams[0] : null;
    const activeChannelId = filter.channels?.length === 1 ? filter.channels[0] : undefined;
    const activeTagId = filter.tags?.length === 1 ? filter.tags[0] : null;

    const select = (handler, value) => () => {
        handler(value);
        onItemSelected?.();
    };

    const views = [
        {
            value: AssignedStatus.Mine,
            label: t('InboxPage.myConversations'),
            icon: <PersonOutlineRoundedIcon />,
            count: counters?.assigned || 0,
        },
        {
            value: AssignedStatus.Unassigned,
            label: t('InboxPage.unassigned'),
            icon: <PersonOffRoundedIcon />,
            count: counters?.unassigned || 0,
        },
        {
            value: AssignedStatus.All,
            label: t('common.all'),
            icon: <InboxRoundedIcon />,
            count: counters?.all || 0,
        },
    ];

    // Cross-team collaborators have no other scope for their conversations; hidden until they actually collaborate.
    const showCollaborating = (counters?.collaborating || 0) > 0 || !!filter.collaboratingOnly;

    const scopedElsewhere =
        !!filter.collaboratingOnly || !!activeTeamId || activeChannelId !== undefined || !!activeTagId;

    const sections = (
        <>
            {isCollapsed ? <SectionDivider /> : <SectionTitle>{t('InboxPage.views')}</SectionTitle>}
            {views.map(view => (
                <RailItem
                    key={view.value}
                    icon={view.icon}
                    label={view.label}
                    count={view.count}
                    active={filter.assignment === view.value && !scopedElsewhere}
                    collapsed={isCollapsed}
                    onClick={select(scopeHandlers.onSelectView, view.value)}
                />
            ))}
            {showCollaborating && (
                <RailItem
                    icon={<PeopleAltOutlinedIcon />}
                    label={t('InboxPage.collaborating')}
                    count={counters?.collaborating || 0}
                    active={!!filter.collaboratingOnly}
                    collapsed={isCollapsed}
                    onClick={select(scopeHandlers.onSelectCollaborating)}
                />
            )}
            {teams.length > 1 && (
                <>
                    {isCollapsed ? <SectionDivider /> : <SectionTitle>{t('filters.teams')}</SectionTitle>}
                    {teams.map(team => (
                        <RailItem
                            key={team.id}
                            icon={<GroupsOutlinedIcon />}
                            label={team.name}
                            count={teamCounts.get(team.id) ?? 0}
                            active={activeTeamId === team.id}
                            collapsed={isCollapsed}
                            onClick={select(scopeHandlers.onSelectTeam, activeTeamId === team.id ? null : team.id)}
                        />
                    ))}
                </>
            )}
            {conversationTags.length > 0 && (
                <>
                    {isCollapsed ? <SectionDivider /> : <SectionTitle>{t('InboxPage.tags')}</SectionTitle>}
                    {conversationTags.map(tag => (
                        <RailItem
                            key={tag.id}
                            icon={
                                <TagDotSlot>
                                    <LabelRoundedIcon sx={{ fontSize: 18, color: tag.color }} />
                                </TagDotSlot>
                            }
                            label={tag.name}
                            count={tagCounts.get(tag.id) ?? 0}
                            active={activeTagId === tag.id}
                            collapsed={isCollapsed}
                            onClick={select(scopeHandlers.onSelectTag, activeTagId === tag.id ? null : tag.id)}
                        />
                    ))}
                </>
            )}
            {isCollapsed ? <SectionDivider /> : <SectionTitle>{t('filters.channels')}</SectionTitle>}
            {incomingChannels.map(channel => (
                <RailItem
                    key={channel.id}
                    icon={getChannelIcon(channel.type)}
                    label={channel.name}
                    active={activeChannelId === channel.id}
                    collapsed={isCollapsed}
                    onClick={select(
                        scopeHandlers.onSelectChannel,
                        activeChannelId === channel.id ? undefined : channel.id,
                    )}
                />
            ))}
            {archived > 0 && (
                <RailItem
                    icon={<ArchiveOutlinedIcon />}
                    label={t('common.archived')}
                    active={activeChannelId === CHANNEL.ARCHIVED}
                    collapsed={isCollapsed}
                    onClick={select(
                        scopeHandlers.onSelectChannel,
                        activeChannelId === CHANNEL.ARCHIVED ? undefined : CHANNEL.ARCHIVED,
                    )}
                />
            )}
        </>
    );

    if (isDrawer) {
        return sections;
    }

    return (
        <RailContainer collapsed={isCollapsed}>
            {!isNarrow && (
                <RailHead collapsed={isCollapsed}>
                    {!isCollapsed && (
                        <Typography variant="subtitle1" component="h1" noWrap>
                            {t('Header.messagingInbox')}
                        </Typography>
                    )}
                    <IconButton size="small" onClick={toggleCollapsed}>
                        {isCollapsed ? (
                            <KeyboardDoubleArrowRightIcon fontSize="small" />
                        ) : (
                            <KeyboardDoubleArrowLeftIcon fontSize="small" />
                        )}
                    </IconButton>
                </RailHead>
            )}
            {sections}
        </RailContainer>
    );
};

export default NavigationRail;
